import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getPlatformDominioWhatsappPricingImpact,
  downloadPlatformDominioWhatsappPricingImpactPdf,
} from "../../../api/client";
import { BusyButton } from "./shared";

const WS_SURFACE_SX = {
  borderRadius: 2,
  boxShadow: (th) => `0 1px 3px ${alpha(th.palette.common.black, th.palette.mode === "dark" ? 0.26 : 0.07)}`,
};

const WS_TABLE_CONTAINER_SX = {
  borderRadius: 1.5,
  border: "1px solid",
  borderColor: "divider",
  overflow: "hidden",
};

const WS_TABLE_DENSE_SX = {
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    color: "text.secondary",
    bgcolor: (th) => alpha(th.palette.action.hover, 0.4),
  },
};

function ymdUtc(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Mês calendário completo anterior (UTC) — alinhado ao backend. */
export function defaultWhatsappImpactPeriod(referenceDate = new Date()) {
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const end = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), 0));
  const start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  return { from: ymdUtc(start), to: ymdUtc(end) };
}

function fmtMoney(value, locale) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(locale, { style: "currency", currency: "BRL" });
}

/** Tarifa contratual R$ 0,049 — não arredondar para 2 casas (evita R$ 0,05). */
function fmtUnitTariff(value, locale) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(locale, {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

/** YYYY-MM-DD → DD/MM/YYYY (exibição BR; inputs date mantêm ISO). */
function formatDateBr(value) {
  const raw = String(value || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return raw || "—";
}

function fmtQty(value, locale) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(locale);
}

function downloadBase64Pdf(base64, filename) {
  const bin = atob(String(base64 || ""));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "Xvision_Previa_WhatsApp_Service.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function chartLabel(ymd) {
  const raw = String(ymd || "");
  const m = raw.match(/^\d{4}-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[1]}` : raw;
}

/**
 * Consumo diário de sessões já cobradas e projeção linear até o fim do mês.
 */
function WhatsappServiceDailyChart({ daily, locale, t }) {
  const points = Array.isArray(daily?.points) ? daily.points : [];
  const chartData = points.map((p) => ({
    ...p,
    label: chartLabel(p.date),
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2.5, ...WS_SURFACE_SX }}>
      <Typography variant="overline" color="text.secondary" fontWeight={700}>
        {t("platformPage.waPricingDailyTitle", {
          date: formatDateBr(daily?.billable_from || "2026-10-01"),
        })}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, maxWidth: 820 }}>
        {daily?.baseline === "reference_period"
          ? t("platformPage.waPricingDailyHintReference", {
              from: formatDateBr(daily?.reference_period?.from),
              to: formatDateBr(daily?.reference_period?.to),
              end: formatDateBr(daily?.period?.to),
              free: fmtQty(daily?.free_allowance, locale),
              numbers: fmtQty(daily?.commercial_numbers, locale),
            })
          : t("platformPage.waPricingDailyHint", {
              from: formatDateBr(daily?.period?.from),
              to: formatDateBr(daily?.period?.to),
              free: fmtQty(daily?.free_allowance, locale),
              numbers: fmtQty(daily?.commercial_numbers, locale),
            })}
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t("platformPage.waPricingDailyCostToDate")}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {fmtMoney(daily?.cost_to_date_brl, locale)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("platformPage.waPricingDailyMessagesToDate", {
              qty: fmtQty(daily?.messages_to_date, locale),
              billable: fmtQty(daily?.billable_to_date, locale),
            })}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t("platformPage.waPricingDailyAvg")}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {fmtQty(daily?.avg_daily_messages, locale)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {daily?.baseline === "reference_period"
              ? t("platformPage.waPricingDailyBaselineAvg", {
                  from: formatDateBr(daily?.reference_period?.from),
                  to: formatDateBr(daily?.reference_period?.to),
                })
              : t("platformPage.waPricingDailyElapsed", {
                  days: fmtQty(daily?.days_elapsed, locale),
                })}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1.2,
            minWidth: 0,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: (th) => alpha(th.palette.warning.main, 0.08),
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t("platformPage.waPricingDailyForecast")}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {fmtMoney(daily?.projected_amount_brl, locale)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("platformPage.waPricingDailyForecastHint", {
              end: formatDateBr(daily?.period?.to),
              messages: fmtQty(daily?.projected_messages, locale),
              billable: fmtQty(daily?.projected_billable, locale),
              remaining: fmtQty(daily?.days_remaining, locale),
            })}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={18} />
            <YAxis yAxisId="left" allowDecimals={false} width={56} />
            <YAxis
              yAxisId="right"
              orientation="right"
              width={64}
              tickFormatter={(v) =>
                Number(v).toLocaleString(locale, {
                  style: "currency",
                  currency: "BRL",
                  maximumFractionDigits: 0,
                })
              }
            />
            <RechartsTooltip
              formatter={(value, name) => {
                if (value == null || value === "") return ["—", name];
                const key = String(name || "");
                const isCost =
                  key === t("platformPage.waPricingDailyCost") ||
                  key === t("platformPage.waPricingDailyCostProjected");
                return [isCost ? fmtMoney(value, locale) : fmtQty(value, locale), name];
              }}
              labelFormatter={(_, payload) => formatDateBr(payload?.[0]?.payload?.date)}
            />
            <Legend />
            <Bar
              yAxisId="left"
              stackId="sessions"
              dataKey="messages"
              name={t("platformPage.waPricingDailySeries")}
              fill="#ed6c02"
              radius={[3, 3, 0, 0]}
            />
            <Bar
              yAxisId="left"
              stackId="sessions"
              dataKey="messages_projected"
              name={t("platformPage.waPricingDailySeriesProjected")}
              fill="#ffcc80"
              radius={[3, 3, 0, 0]}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="cost_brl"
              name={t("platformPage.waPricingDailyCost")}
              stroke="#1565c0"
              strokeWidth={2}
              dot={{ r: 2 }}
              connectNulls={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="cost_projected_brl"
              name={t("platformPage.waPricingDailyCostProjected")}
              stroke="#90caf9"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25 }}>
        {daily?.baseline === "reference_period"
          ? t("platformPage.waPricingDailyMethodReference", {
              avg: fmtQty(daily?.avg_daily_messages, locale),
              days: fmtQty(daily?.days_in_month, locale),
              end: formatDateBr(daily?.period?.to),
              tariff: fmtUnitTariff(daily?.unit_tariff_brl, locale),
            })
          : t("platformPage.waPricingDailyMethod", {
              avg: fmtQty(daily?.avg_daily_messages, locale),
              elapsed: fmtQty(daily?.days_elapsed, locale),
              tariff: fmtUnitTariff(daily?.unit_tariff_brl, locale),
            })}
      </Typography>
    </Paper>
  );
}

/**
 * Aba: prévia do que o cliente pagará a mais (repasse Service R$ 0,049) + PDF timbrado.
 */
export default function WorkspaceWhatsappPricingImpactTab({ dominioId, locale = "pt-BR" }) {
  const { t } = useTranslation("app");
  const defaults = useMemo(() => defaultWhatsappImpactPeriod(), []);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfError, setPdfError] = useState("");

  const load = useCallback(async () => {
    if (!dominioId || !from || !to) return;
    setLoading(true);
    setError("");
    try {
      const data = await getPlatformDominioWhatsappPricingImpact(dominioId, { from, to });
      setPayload(data ?? {});
    } catch (err) {
      setPayload(null);
      setError(
        err?.response?.data?.message || err?.message || t("platformPage.waPricingLoadError")
      );
    } finally {
      setLoading(false);
    }
  }, [dominioId, from, to, t]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = useCallback(async () => {
    if (!dominioId || !from || !to) return;
    setPdfLoading(true);
    setPdfError("");
    try {
      const data = await downloadPlatformDominioWhatsappPricingImpactPdf(dominioId, { from, to });
      if (!data?.base64) throw new Error(t("platformPage.waPricingPdfError"));
      downloadBase64Pdf(data.base64, data.filename);
    } catch (err) {
      setPdfError(
        err?.response?.data?.message || err?.message || t("platformPage.waPricingPdfError")
      );
    } finally {
      setPdfLoading(false);
    }
  }, [dominioId, from, to, t]);

  if (!dominioId) return null;

  const volumes = payload?.volumes || {};
  const partner = payload?.partner || {};
  const education = payload?.education || {};
  const pass = payload?.client?.service_passthrough || null;
  const partnerUnavailable = pass?.volume_source === "partner_unavailable";
  const metaBillableFrom = payload?.meta_service_billable_from || pass?.billable_from || "2026-10-01";

  const volumeSourceLabel =
    pass?.volume_source === "gupshup_partner"
      ? t("platformPage.waPricingCostSourceGupshup")
      : pass?.volume_source === "partner_unavailable"
        ? t("platformPage.waPricingCostSourcePartnerUnavailable")
        : t("platformPage.waPricingCostSourceTenant");
  const xvision = payload?.xvision || {};
  const categories = Array.isArray(payload?.categories) ? payload.categories : [];

  return (
    <Stack spacing={2.5} maxWidth={1100}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <TrendingUpOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("platformPage.waPricingTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
              {t("platformPage.waPricingIntro", { date: formatDateBr(metaBillableFrom) })}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <BusyButton
            size="small"
            variant="outlined"
            onClick={() => load()}
            loading={loading}
            loadingText={t("billingPage.refresh")}
          >
            {t("billingPage.refresh")}
          </BusyButton>
          <BusyButton
            size="small"
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={() => downloadPdf()}
            loading={pdfLoading}
            loadingText={t("platformPage.waPricingPdfLoading")}
            disabled={!pass}
          >
            {t("platformPage.waPricingPdfDownload")}
          </BusyButton>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap">
        <TextField
          size="small"
          type="date"
          label={t("platformPage.waPricingFrom")}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label={t("platformPage.waPricingTo")}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <BusyButton size="small" variant="outlined" onClick={() => load()} loading={loading}>
          {t("platformPage.waPricingApplyPeriod")}
        </BusyButton>
        {payload?.period?.from ? (
          <Chip
            size="small"
            variant="outlined"
            label={t("platformPage.waPricingPeriodChip", {
              from: formatDateBr(payload.period.from),
              to: formatDateBr(payload.period.to),
            })}
            sx={{ fontWeight: 500 }}
          />
        ) : null}
      </Stack>

      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {pdfError ? (
        <Alert severity="error" onClose={() => setPdfError("")}>
          {pdfError}
        </Alert>
      ) : null}

      {loading && !payload ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {t("platformPage.waPricingLoading")}
          </Typography>
        </Stack>
      ) : null}

      {payload?.client?.service_daily?.available ? (
        <WhatsappServiceDailyChart daily={payload.client.service_daily} locale={locale} t={t} />
      ) : payload?.client?.service_daily?.reason === "partner_unavailable" ||
        payload?.client?.service_daily?.reason === "daily_breakdown_missing" ? (
        <Alert severity="info" variant="outlined">
          {payload.client.service_daily.reason === "daily_breakdown_missing"
            ? t("platformPage.waPricingDailyMissingDates")
            : payload.client.service_daily.message || t("platformPage.waPricingDailyPartnerMissing")}
        </Alert>
      ) : null}

      {payload && pass ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            ...WS_SURFACE_SX,
            bgcolor: (th) => alpha(th.palette.warning.main, 0.06),
            borderColor: (th) => alpha(th.palette.warning.main, 0.35),
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={700}>
            {t("platformPage.waPricingPassTitle")}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, mb: 0.5 }}>
            {fmtMoney(pass.delta_brl, locale)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 820 }}>
            {t("platformPage.waPricingPassHeroHint", {
              date: formatDateBr(metaBillableFrom),
              tariff: fmtUnitTariff(pass.unit_tariff_brl, locale),
            })}
          </Typography>
          {partnerUnavailable ? (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              {partner.message || t("platformPage.waPricingPartnerUnavailablePass")}
            </Alert>
          ) : null}
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            <Chip size="small" color="success" variant="outlined" label={t("platformPage.waPricingPassMrrUnchanged")} />
            <Chip
              size="small"
              variant="outlined"
              label={t("platformPage.waPricingPassTodayZero", {
                value: fmtMoney(pass.amount_today_brl, locale),
              })}
            />
            <Chip
              size="small"
              color="warning"
              variant="filled"
              label={t("platformPage.waPricingPassMonthlyAvg", {
                value: fmtMoney(pass.monthly_average_brl, locale),
              })}
            />
          </Stack>

          <TableContainer sx={WS_TABLE_CONTAINER_SX}>
            <Table size="small" sx={WS_TABLE_DENSE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("platformPage.waPricingColDesc")}</TableCell>
                  <TableCell align="right">{t("platformPage.waPricingColCalc")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{t("platformPage.waPricingPassRowService")}</TableCell>
                  <TableCell align="right">{fmtQty(pass.service_messages, locale)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("platformPage.waPricingPassRowFranchise", {
                      per: fmtQty(pass.free_per_number_month, locale),
                      numbers: fmtQty(pass.commercial_numbers, locale),
                      months: fmtQty(pass.months_in_period, locale),
                    })}
                  </TableCell>
                  <TableCell align="right">− {fmtQty(pass.free_allowance, locale)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("platformPage.waPricingPassRowBillable")}</TableCell>
                  <TableCell align="right">{fmtQty(pass.billable_messages, locale)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("platformPage.waPricingPassRowTariff")}</TableCell>
                  <TableCell align="right">{fmtUnitTariff(pass.unit_tariff_brl, locale)}</TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: (th) => alpha(th.palette.warning.main, 0.08) }}>
                  <TableCell sx={{ fontWeight: 700 }}>{t("platformPage.waPricingPassRowAmount")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {fmtMoney(pass.amount_brl, locale)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25 }}>
            {pass.disclaimer || t("platformPage.waPricingPassDisclaimer")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {t("platformPage.waPricingPassVolumeSource", {
              source: volumeSourceLabel,
            })}
          </Typography>
        </Paper>
      ) : null}

      {payload ? (
        <>
          <Paper variant="outlined" sx={{ p: 2, ...WS_SURFACE_SX }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t("platformPage.waPricingVolumesTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("platformPage.waPricingVolumesSub")}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip
                size="small"
                color="warning"
                label={t("platformPage.waPricingGupshupCatService", {
                  qty: fmtQty(
                    partner?.categories?.service != null ? partner.categories.service : 0,
                    locale
                  ),
                })}
              />
              {partner?.categories ? (
                <>
                  {partner.categories.incoming != null ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("platformPage.waPricingGupshupCatIncoming", {
                        qty: fmtQty(partner.categories.incoming, locale),
                      })}
                    />
                  ) : null}
                  {partner.categories.outgoing != null ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("platformPage.waPricingGupshupCatOutgoing", {
                        qty: fmtQty(partner.categories.outgoing, locale),
                      })}
                    />
                  ) : null}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("platformPage.waPricingGupshupCat", {
                      cat: "utility",
                      qty: fmtQty(partner.categories.utility, locale),
                    })}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("platformPage.waPricingGupshupCat", {
                      cat: "marketing",
                      qty: fmtQty(partner.categories.marketing, locale),
                    })}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("platformPage.waPricingGupshupCat", {
                      cat: "authentication",
                      qty: fmtQty(partner.categories.authentication, locale),
                    })}
                  />
                </>
              ) : null}
              <Chip
                size="small"
                variant="outlined"
                label={t("platformPage.waPricingVolInteractionsShort", {
                  qty: fmtQty(volumes.interactions, locale),
                })}
              />
            </Stack>
            {partner?.message && !partner?.categories ? (
              <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                {partner.message}
              </Alert>
            ) : null}

            {Array.isArray(partner?.apps) && partner.apps.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t("platformPage.waPricingPartnerAppsTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  {t("platformPage.waPricingPartnerAppsHint", {
                    appsOk: fmtQty(partner?.meta?.apps_ok, locale),
                    appsTried: fmtQty(partner?.meta?.apps_tried, locale),
                    phones: fmtQty(
                      partner?.meta?.phones_ok_unique || pass?.commercial_numbers,
                      locale
                    ),
                    channels: fmtQty(partner?.meta?.channels_scanned, locale),
                  })}
                </Typography>
                <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={WS_TABLE_DENSE_SX}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("platformPage.waPricingPartnerColChannel")}</TableCell>
                        <TableCell>{t("platformPage.waPricingPartnerColPhone")}</TableCell>
                        <TableCell>{t("platformPage.waPricingPartnerColApp")}</TableCell>
                        <TableCell align="right">{t("platformPage.waPricingPartnerColService")}</TableCell>
                        <TableCell align="right">utility</TableCell>
                        <TableCell align="right">marketing</TableCell>
                        <TableCell align="right">auth</TableCell>
                        <TableCell>{t("platformPage.waPricingPartnerColStatus")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {partner.apps.map((app) => (
                        <TableRow key={`${app.channel_id}-${app.partner_app_id}`}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {app.channel_name || `Canal ${app.channel_id || "—"}`}
                            </Typography>
                            {app.channel_type ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {app.channel_type}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{app.phone || "—"}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                              {app.partner_app_id || app.appidgupshup || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {app.status === "ok" ? fmtQty(app.categories?.service, locale) : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {app.status === "ok" ? fmtQty(app.categories?.utility, locale) : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {app.status === "ok" ? fmtQty(app.categories?.marketing, locale) : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {app.status === "ok" ? fmtQty(app.categories?.authentication, locale) : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={app.status === "ok" ? "success" : "default"}
                              variant={app.status === "ok" ? "filled" : "outlined"}
                              label={t(`platformPage.waPricingPartnerStatus.${app.status}`, {
                                defaultValue: app.status || "—",
                              })}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null}
          </Paper>

          <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t("platformPage.waPricingInternalCmvTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t("platformPage.waPricingInternalCmvHint")}
              </Typography>
              <Chip
                size="small"
                sx={{ mb: 1.5 }}
                label={t("platformPage.waPricingDeltaCmv", {
                  value: fmtMoney(xvision.delta_brl, locale),
                  pct:
                    Number.isFinite(Number(xvision.delta_pct))
                      ? `${Number(xvision.delta_pct).toLocaleString(locale, { maximumFractionDigits: 1 })}%`
                      : "—",
                })}
              />
              {categories.length > 0 ? (
                <TableContainer sx={WS_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={WS_TABLE_DENSE_SX}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("platformPage.waPricingColCategory")}</TableCell>
                        <TableCell align="right">{t("platformPage.waPricingColQty")}</TableCell>
                        <TableCell align="right">{t("platformPage.waPricingColCmvPre")}</TableCell>
                        <TableCell align="right">{t("platformPage.waPricingColCmvPost")}</TableCell>
                        <TableCell align="right">{t("platformPage.waPricingColDelta")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categories.map((row) => (
                        <TableRow key={row.metric_key}>
                          <TableCell>
                            {row.metric_key === "interactions"
                              ? t("platformPage.waPricingCatService")
                              : t(`billingPage.metricKeys.${row.metric_key}`, {
                                  defaultValue: String(row.metric_key || "").replace(/_/g, " "),
                                })}
                          </TableCell>
                          <TableCell align="right">{fmtQty(row.quantity, locale)}</TableCell>
                          <TableCell align="right">{fmtMoney(row.cmv_total_pre_brl, locale)}</TableCell>
                          <TableCell align="right">{fmtMoney(row.cmv_total_post_brl, locale)}</TableCell>
                          <TableCell align="right">{fmtMoney(row.cmv_delta_brl, locale)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </AccordionDetails>
          </Accordion>

          <Accordion variant="outlined" disableGutters sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t("platformPage.waPricingEducationTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              <Stack spacing={1.25}>
                {[
                  education.narrative || t("platformPage.waPricingEduNarrative"),
                  education.tariff || t("platformPage.waPricingEduTariff"),
                  education.franchise || t("platformPage.waPricingEduFranchise"),
                  education.service_vs_interactions || t("platformPage.waPricingEduServiceVsIx"),
                  education.metering || t("platformPage.waPricingEduMetering"),
                  education.oct_2026 || t("platformPage.waPricingEduOct"),
                  education.inbound_free || t("platformPage.waPricingEduInbound"),
                ].map((txt) => (
                  <Typography key={txt.slice(0, 40)} variant="body2" color="text.secondary">
                    {txt}
                  </Typography>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </>
      ) : null}
    </Stack>
  );
}
